<template>
  <div class="drop" :class="{ 'drop--vip': isVipDrop }" @click="handleClick">
    <!-- Front face -->
    <div class="front" :class="shortRarity">
      <div class="game-mode" :class="shortRarity">
        <i
          v-if="isUpgrade"
          class="fa-solid fa-arrow-trend-up"
          aria-hidden="true"
          title="Upgrade drop"
        ></i>
        <i
          v-else-if="isArenaDrop"
          class="fa-solid fa-people-group"
          aria-hidden="true"
          :title="t('WHALE_DUEL.liveDrop.title')"
        ></i>
        <i v-else-if="!isVipCase" class="fa-solid fa-box-open" aria-hidden="true"></i>
        <span v-else class="game-mode__vip">
          <i class="fa-solid fa-gem" aria-hidden="true"></i>
        </span>
      </div>

      <img
        class="weapon-image"
        :src="itemImageSrc"
        :alt="itemAltText"
        loading="lazy"
        height="42"
      />
      <span class="weapon-name">{{ itemWeapon }}</span>
      <span class="item-name">{{ itemSkin }}</span>
    </div>

    <!-- Backface -->
    <div class="back" :class="shortRarity">
      <div class="back-images">
        <img
          class="case-img"
          :src="caseImageSrc"
          :alt="caseAltText"
          loading="lazy"
        />
        <img
          class="user-img"
          :src="userAvatarSrc"
          :alt="data.user?.name || 'User avatar'"
          loading="lazy"
        />
      </div>
      <span class="user-name">
        <span class="user-name__text">{{ data.user?.name }}</span>
        <span v-if="data.user?.isVip" class="user-name__badge">
          <VipBadge />
        </span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { RouteName } from '@/router/types'
import { encodeCaseName } from '@/utils/caseNameUrl'
import { LiveDrop } from '@/services/models/models'
import { getSteamImageUrl } from '@/constants/urls'
import logoNoText from '@/assets/logo-wo-text.png'
import VipBadge from '@/components/VipBadge/VipBadge.vue'

defineOptions({ name: 'LiveDropItem' })

// --- Props ---

const props = defineProps<{
  data: LiveDrop
}>()
const { t } = useI18n()
const router = useRouter()

// --- Computed ---
const shortRarity = computed(
  () => props.data.item?.rarity?.toLowerCase().split(' ')[0] ?? '',
)
const isVipDrop = computed(() => Boolean(props.data.user?.isVip))
const isVipCase = computed(() => Boolean(props.data.case?.isVip))
const isUpgrade = computed(() => Boolean(props.data.isUpgrade))
const isArenaDrop = computed(() => Boolean(props.data.isArena))
const isBotUser = computed(() => {
  const user = (props.data as any)?.user
  if (!user) return false
  if (Boolean(user.isBot)) return true
  return String(user.name ?? '').toLowerCase().includes('bot')
})

const itemWeapon = computed(() => props.data.item?.weapon ?? '')
const itemSkin = computed(() => props.data.item?.skin ?? '')
const itemImageSrc = computed(() => {
  const itemImage = props.data.item?.image
  return itemImage ? getSteamImageUrl(itemImage) : ''
})
const itemAltText = computed(() => itemSkin.value || itemWeapon.value || 'Item')

const caseImageSrc = computed(() => {
  const caseImage = props.data.case?.image
  if (caseImage) return caseImage.replace('/media/cases/', '/media/cases-new/')

  const itemImage = props.data.item?.image
  return itemImage ? getSteamImageUrl(itemImage) : ''
})

const caseAltText = computed(() => props.data.case?.name ?? (isUpgrade.value ? 'Upgrade item' : 'Case'))

const userAvatarSrc = computed(() => {
  if (isBotUser.value) return logoNoText
  return props.data.user?.avatar || 'https://www.gravatar.com/avatar/?d=mp&f=y'
})

const caseSlug = computed(() => {
  const caseName = props.data.case?.name
  return caseName ? encodeCaseName(caseName) : null
})

function handleClick() {
  const slug = caseSlug.value
  if (!slug) return

  const isDaily = props.data.case?.isDailyCase
  const routeName = isDaily ? RouteName.DAILY_FREE_DETAIL : RouteName.CASE_DETAIL

  router.push({
    name: routeName,
    params: { caseName: slug },
  })
}
</script>

<style scoped lang="scss">
@use 'sass:color';

.drop {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  flex-grow: 0;
  width: 100px;
  height: 100%;
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  .front,
  .back {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    backface-visibility: hidden;
    transition:
      transform 0.6s ease,
      opacity 0.6s ease;
    border-radius: 6px;
    padding: 6px;
    width: 100px;

    &.consumer {
      background: linear-gradient(
        $rarity-consumer,
        color.scale($rarity-consumer, $lightness: -10%)
      );
      color: $rarity-text-dark;
    }

    &.industrial {
      background: linear-gradient(
        $rarity-industrial,
        color.scale($rarity-industrial, $lightness: -10%)
      );
      color: $rarity-text-light;
    }

    &.mil-spec {
      background: linear-gradient(
        $rarity-mil-spec,
        color.scale($rarity-mil-spec, $lightness: -10%)
      );
      color: $rarity-text-light;
    }

    &.restricted {
      background: linear-gradient(
        $rarity-restricted,
        color.scale($rarity-restricted, $lightness: -10%)
      );
      color: $rarity-text-light;
    }

    &.classified {
      background: linear-gradient(
        $rarity-classified,
        color.scale($rarity-classified, $lightness: -10%)
      );
      color: $rarity-text-light;
    }

    &.covert {
      background: linear-gradient($rarity-covert, color.scale($rarity-covert, $lightness: -10%));
      color: $rarity-text-light;
    }

    &.contraband {
      background: linear-gradient(
        $rarity-contraband,
        color.scale($rarity-contraband, $lightness: -10%)
      );
      color: $rarity-text-light;
    }

    &.extraordinary {
      background: linear-gradient(
        $rarity-exceedingly,
        color.scale($rarity-exceedingly, $lightness: -10%)
      );
      color: $rarity-text-dark;
    }
  }

.front {
    position: relative;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background-color: $elevation2;
    color: $text-light-100;
    border-radius: 6px;
    overflow: hidden;
    justify-content: space-between;
    padding: 8px 6px;

    .game-mode {
      font-size: 9px;
      color: rgba(255, 255, 255, 0.7);
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
      text-align: center;
      display: inline-flex;
      align-items: center;
      gap: 4px;

      &__vip {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.15);
        color: #fbbf24;
        font-size: 0.65rem;
      }
    }

    .weapon-image {
      object-fit: contain;
      border-radius: 4px;
      width: 100%;
      height: 48px;
      filter: saturate(1.18) contrast(1.06) drop-shadow(0 6px 14px rgba(0, 0, 0, 0.38));
      transition:
        transform 0.25s ease,
        filter 0.25s ease;
    }

    .weapon-name {
      text-align: center;
      font-size: 9px;
      color: $text-light-200;
      text-shadow: 0 1px 0 rgba(0, 0, 0, 0.35);
      letter-spacing: 0.02em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
      line-height: 1.2;
    }

    .item-name {
      text-align: center;
      font-size: 10px;
      font-weight: 700;
      color: $text-light-200;
      text-shadow: 0 1px 0 rgba(0, 0, 0, 0.35);
      letter-spacing: 0.02em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
      line-height: 1.2;
    }
  }

  .back {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transform: rotateY(180deg);
    opacity: 0;
    background: $elevation2;
    color: $text-light-200;
    font-size: 12px;
    text-align: center;
    border: 1px solid rgba(255, 255, 255, 0.1);

    .back-images {
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      width: 74px;
      height: 74px;
      overflow: hidden;
      border-radius: 6px;
    }

    .case-img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: 6px;
    }
    .user-img {
      position: absolute;
      top: 80%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: 2px solid rgba(255, 255, 255, 0.2);
      box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
      object-fit: cover;
    }

    .user-name {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      font-size: 10px;
      font-weight: 500;
      color: #fff;
      width: 100%;
      max-width: 88px;
      text-align: center;
    }

    .user-name__text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .user-name__badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;

      :deep(.vip-badge i) {
        font-size: 0.9rem;
        color: #ffd580;
      }
    }
  }

  // Flip on hover
  &:hover .front {
    transform: rotateY(180deg);
    opacity: 0;
  }

  &:hover .back {
    transform: rotateY(0deg);
    opacity: 1;
  }

  &.drop--vip {
    .front,
    .back {
      border: 1px solid rgba(255, 214, 128, 0.7);
    }

    .game-mode,
    .weapon-name,
    .item-name,
    .user-name {
      text-shadow: 0 0 6px rgba(0, 0, 0, 0.6);
    }
  }
}
</style>
